// POLICY-COVERAGE-V1 / POL-5 (2026-05-15) — Policy & Coverage tab.
// POLICY-EDIT-V2 (2026-06-16) — entry/edit added: attach/detach a policy,
// create/edit a policy master, manage insureds, add/edit/delete coverage
// lines. Editing is gated on the MANAGE_POLICIES capability; the read-only
// rendering below is unchanged for users without it. Writes flip a vca_lift
// row to app_authored server-side so the nightly reconcile leaves it alone.
//
// Reads consolidated payload from GET /claims/{id}/policy-coverage.
//
// Display rules (per Kuhrt amendments 2026-05-15):
//   * Limit (coverage_amount) — hide when 0 (61% of VCA rows are zero,
//     i.e. placeholder, not authoritative).  Show "—" instead.
//   * Slip — only render the section if claim_parties has actual rows.
//
import { useState, useEffect, useCallback } from "react";
import { apiFetch, fmt, can } from "../api";
import EntityDocuments from "./EntityDocuments";  // DC-3: policy document lens
import { Spinner, EmptyState, SectionHeader, StatusPill } from "./ui";  // UI-SWEEP-CLAIM

const LAYER_TYPES = ["primary", "mezzanine", "excess"];
export default function PolicyCoverageTab({ claimId, workRefresh, onWorkChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);   // {type, ...}
  const canManage = can("manage_policies");

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/claims/${claimId}/policy-coverage`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message || "Failed to load policy & coverage."); setLoading(false); });
  }, [claimId]);

  useEffect(() => { load(); }, [load]);

  // Silent re-sync on a global work bump — no spinner, so an open edit modal
  // (or the page) isn't clobbered when the bump arrives from window refocus.
  useEffect(() => {
    if (!workRefresh) return;
    apiFetch(`/claims/${claimId}/policy-coverage`).then(setData).catch(() => {});
  }, [claimId, workRefresh]);

  // Policy/coverage changes feed the financial ribbon (deductible) + Overview
  // Policy tile — bump the workspace counter alongside the local reload.
  const changed = () => { load(); onWorkChanged && onWorkChanged(); };
  const afterChange = () => { setModal(null); changed(); };

  if (loading) return <Spinner label="Loading policy & coverage…" />;
  if (err)     return <div className="alert-error">{err}</div>;
  if (!data)   return null;

  const { attachments = [], coverages = [], slip = [] } = data;

  const coveragesByPolicy = {};
  const unattachedCoverages = [];
  for (const c of coverages) {
    if (c.policy_id) {
      (coveragesByPolicy[c.policy_id] = coveragesByPolicy[c.policy_id] || []).push(c);
    } else {
      unattachedCoverages.push(c);
    }
  }

  const empty = attachments.length === 0 && coverages.length === 0;

  return (
    <div className="tab-content" style={{ padding: 20 }}>
      {canManage && (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 16 }}>
          <button className="btn btn-sm btn-outline" onClick={() => setModal({ type: "attach" })}>
            + Attach policy
          </button>
        </div>
      )}

      {empty ? (
        <NoPolicyEmpty canManage={canManage} onAttach={() => setModal({ type: "attach" })} />
      ) : (
        <>
          {attachments.map(att => (
            <PolicyCard key={att.attachment_id} attachment={att}
                        coverages={coveragesByPolicy[att.policy_id] || []}
                        canManage={canManage} claimId={claimId} setModal={setModal} onReload={changed} />
          ))}

          {unattachedCoverages.length > 0 && (
            <UnattachedCoveragesCard coverages={unattachedCoverages}
                                     canManage={canManage} claimId={claimId} setModal={setModal} onReload={changed} />
          )}

          <SlipSection slip={slip} />
        </>
      )}

      {modal?.type === "attach" && (
        <AttachPolicyModal claimId={claimId} onClose={() => setModal(null)} onDone={afterChange} />
      )}
      {modal?.type === "editPolicy" && (
        <PolicyEditModal policyId={modal.policyId} onClose={() => setModal(null)} onDone={afterChange} />
      )}
      {modal?.type === "coverage" && (
        <CoverageFormModal claimId={claimId} coverage={modal.coverage} defaultPolicyId={modal.policyId}
                           onClose={() => setModal(null)} onDone={afterChange} />
      )}
      {modal?.type === "detach" && (
        <DetachModal claimId={claimId} attachment={modal.attachment}
                     onClose={() => setModal(null)} onDone={afterChange} />
      )}
    </div>
  );
}

// ─── Components ────────────────────────────────────────────────────────

function PolicyCard({ attachment, coverages, canManage, claimId, setModal, onReload }) {
  const isPrimary = attachment.is_primary;
  const layerLabel = attachment.layer_type
    ? attachment.layer_type.charAt(0).toUpperCase() + attachment.layer_type.slice(1)
    : (isPrimary ? "Primary attachment" : null);
  const layerColor = layerLabel === "Mezzanine"
    ? "#1565C0" : (layerLabel === "Excess" ? "#6A1B9A" : "#2E7D32");

  return (
    <div className="card" style={{
      marginBottom: 20, padding: 16,
      borderLeft: `4px solid ${layerColor}`,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--text2,#555)" }}>
            Policy
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--navy,#1F3864)" }}>
            {attachment.policy_number || <span style={{ color: "var(--text3,#888)" }}>—</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {layerLabel && (
            <StatusPill sm tone={layerLabel === "Mezzanine" ? "blue" : (layerLabel === "Excess" ? "slate" : "green")}>{layerLabel}</StatusPill>
          )}
          {canManage && (
            <>
              <button className="btn btn-xs btn-outline"
                      onClick={() => setModal({ type: "editPolicy", policyId: attachment.policy_id })}>
                Edit policy
              </button>
              <button className="btn btn-xs btn-outline" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }}
                      onClick={() => setModal({ type: "detach", attachment })}>
                Detach
              </button>
            </>
          )}
        </div>
      </div>

      <FieldGrid rows={[
        ["Period", periodLabel(attachment.inception_date, attachment.expiration_date)],
        ["Type", attachment.policy_type || muted("Not recorded")],
        attachment.primary_policy_number ? [
          "Over primary",
          <span><strong>{attachment.primary_policy_number}</strong>
            {attachment.primary_inception_date &&
              <span style={{ color: "var(--text3,#888)" }}> · {fmt.date(attachment.primary_inception_date)}</span>}
          </span>
        ] : null,
        attachment.fleet_program_note ? ["Fleet / Program", attachment.fleet_program_note] : null,
        attachment.policy_notes ? ["Notes", attachment.policy_notes] : null,
        attachment.attachment_notes ? ["Attachment note", attachment.attachment_notes] : null,
        ["Insureds", attachment.insureds.length === 0
          ? muted("No insureds recorded on this policy.")
          : (
            <div>{attachment.insureds.map((ins, i) => (
              <div key={ins.company_id} style={{ marginBottom: i < attachment.insureds.length - 1 ? 4 : 0 }}>
                {ins.company_name}
                {ins.is_primary && <Badge color="#2E75B6" text="Primary" />}
              </div>
            ))}</div>
          )
        ],
      ].filter(Boolean)} />

      {/* Coverage lines for this policy */}
      <div style={{ marginTop: 16 }}>
        <SectionHeader title="Coverage lines on this claim"
          actions={canManage && (
            <button className="btn btn-xs btn-outline"
                    onClick={() => setModal({ type: "coverage", policyId: attachment.policy_id })}>
              + Add coverage
            </button>
          )} />
        {coverages.length === 0 ? (
          <EmptyState icon="📋" title="No coverage data"
            hint="No coverage data recorded against this policy on this claim." />
        ) : (
          <CoverageTable coverages={coverages} canManage={canManage} claimId={claimId} setModal={setModal} onReload={onReload} />
        )}
      </div>
    </div>
  );
}

function UnattachedCoveragesCard({ coverages, canManage, claimId, setModal, onReload }) {
  return (
    <div className="card" style={{ marginBottom: 20, padding: 16, borderLeft: "4px solid var(--text3,#888)" }}>
      <SectionHeader title="Unattached Coverage Lines"
        actions={canManage && (
          <button className="btn btn-xs btn-outline"
                  onClick={() => setModal({ type: "coverage", policyId: null })}>
            + Add coverage
          </button>
        )} />
      <div style={{ fontSize: 12, color: "var(--text3,#888)", marginBottom: 12, fontStyle: "italic" }}>
        Coverage rows recorded for this claim but not yet linked to a specific policy.
      </div>
      <CoverageTable coverages={coverages} canManage={canManage} claimId={claimId} setModal={setModal} onReload={onReload} />
    </div>
  );
}

function CoverageTable({ coverages, canManage, claimId, setModal, onReload }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: "2px solid var(--border,#DDE2E8)" }}>
          <Th>Type</Th>
          <Th align="right">Limit</Th>
          <Th align="right">Per-Claim Deductible</Th>
          <Th>Applies to Loss</Th>
          <Th>Notes</Th>
          {canManage && <Th align="right">Actions</Th>}
        </tr>
      </thead>
      <tbody>
        {coverages.map(c => (
          <tr key={c.id} style={{ borderBottom: "1px solid var(--border-light,#EEF1F4)" }}>
            <Td><strong>{c.coverage_type}</strong></Td>
            <Td align="right">{limitDisplay(c.coverage_amount)}</Td>
            <Td align="right">{deductibleDisplay(c.per_claim_deductible)}</Td>
            <Td>
              {c.applies_to_loss
                ? <span style={{ color: "#2E7D32", fontWeight: 700 }}>✓</span>
                : <span style={{ color: "var(--text3,#888)" }}>—</span>
              }
            </Td>
            <Td>
              {c.notes
                ? <span style={{ fontSize: 12 }}>{c.notes}</span>
                : <span style={{ color: "var(--text3,#888)" }}>—</span>}
            </Td>
            {canManage && (
              <Td align="right">
                <button className="btn btn-xs btn-outline"
                        onClick={() => setModal({ type: "coverage", coverage: c, policyId: c.policy_id })}>
                  Edit
                </button>{" "}
                <button className="btn btn-xs btn-outline" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }}
                        onClick={() => deleteCoverage(claimId, c, onReload)}>
                  Delete
                </button>
              </Td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function deleteCoverage(claimId, c, onReload) {
  if (!window.confirm(`Delete the "${c.coverage_type}" coverage line?`)) return;
  apiFetch(`/claims/${claimId}/coverages/${c.id}`, { method: "DELETE" })
    .then(() => onReload && onReload())
    .catch(e => alert(e.message || "Delete failed."));
}

function SlipSection({ slip }) {
  const hasSlip = slip && slip.length > 0;
  return (
    <div className="card" style={{ padding: 16, marginBottom: 0 }}>
      <SectionHeader title="Slip Participation" subtitle="mirrored from Parties tab" />
      {!hasSlip ? (
        <EmptyState icon="📋" title="No slip participation" hint="No slip participation recorded on this claim." />
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            {slip.map(s => (
              <tr key={s.id} style={{ borderBottom: "1px solid var(--border-light,#EEF1F4)" }}>
                <Td>{s.company_name || <span style={{ color: "var(--text3,#888)" }}>(no company)</span>}</Td>
                <Td align="right" style={{ fontFamily: "monospace", width: 80 }}>
                  {s.participation_pct != null
                    ? Number(s.participation_pct).toFixed(1) + "%"
                    : <span style={{ color: "var(--text3,#888)" }}>—</span>}
                </Td>
                <Td style={{ width: 60 }}>
                  {s.is_lead && <Badge color="#1F3864" text="Lead" />}
                </Td>
                <Td style={{ fontSize: 12, color: "var(--text2,#555)" }}>
                  {s.reference_number ? `Ref: ${s.reference_number}` : ""}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NoPolicyEmpty({ canManage, onAttach }) {
  return (
    <div className="tab-content" style={{ padding: 0 }}>
      <div className="card" style={{
        padding: 32, textAlign: "center",
        background: "var(--bg,#F8F9FB)",
        color: "var(--text2,#555)",
      }}>
        <div style={{ fontSize: 32, color: "var(--text3,#888)", marginBottom: 8 }}>📋</div>
        <div style={{ fontWeight: 600, marginBottom: 6, color: "var(--text,#1A1A1A)" }}>
          No policy or coverage data for this claim
        </div>
        <div style={{ fontSize: 13, marginBottom: canManage ? 16 : 0 }}>
          When a policy is attached to this claim, this tab will show the
          policy header, coverage lines, and slip participation.
        </div>
        {canManage && (
          <button className="btn btn-sm btn-primary" onClick={onAttach}>+ Attach a policy</button>
        )}
      </div>
    </div>
  );
}

// ─── Modals ─────────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, width = 560 }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
      display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, padding: "6vh 16px",
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{
        background: "white", width, maxWidth: "100%", maxHeight: "86vh", overflowY: "auto", padding: 20,
        borderRadius: 8, boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "var(--navy,#1F3864)" }}>{title}</h3>
          <button className="btn btn-xs btn-outline" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text2,#555)", marginBottom: 4 }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "var(--text3,#888)", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
const inputStyle = { width: "100%", padding: "7px 9px", fontSize: 13, border: "1px solid var(--border,#DDE2E8)", borderRadius: 5, boxSizing: "border-box" };

function ErrBar({ msg }) {
  if (!msg) return null;
  return <div style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5", padding: "8px 10px", borderRadius: 5, fontSize: 13, marginBottom: 12 }}>{msg}</div>;
}

function AttachPolicyModal({ claimId, onClose, onDone }) {
  const [tab, setTab] = useState("existing");   // existing | new
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ policy_number: "", policy_type: "", layer_type: "", inception_date: "", expiration_date: "", notes: "" });

  useEffect(() => {
    if (tab !== "existing") return;
    const t = setTimeout(() => {
      apiFetch(`/policies/search?q=${encodeURIComponent(q)}&limit=15`)
        .then(d => setHits(d.results || [])).catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q, tab]);

  const attach = (policyId) => {
    setBusy(true); setErr(null);
    apiFetch(`/claims/${claimId}/policies`, { method: "POST", body: { policy_id: policyId, is_primary: true } })
      .then(onDone).catch(e => { setErr(e.message || "Attach failed."); setBusy(false); });
  };

  const createAndAttach = () => {
    if (!form.policy_number.trim()) { setErr("Policy number is required."); return; }
    setBusy(true); setErr(null);
    const body = { ...form };
    Object.keys(body).forEach(k => { if (body[k] === "") delete body[k]; });
    apiFetch("/policies", { method: "POST", body })
      .then(r => attach(r.policy.id))
      .catch(e => { setErr(e.message || "Create failed."); setBusy(false); });
  };

  return (
    <ModalShell title="Attach a policy" onClose={onClose}>
      <ErrBar msg={err} />
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button className={`btn btn-sm ${tab === "existing" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("existing")}>Existing policy</button>
        <button className={`btn btn-sm ${tab === "new" ? "btn-primary" : "btn-outline"}`} onClick={() => setTab("new")}>New policy</button>
      </div>

      {tab === "existing" ? (
        <>
          <Field label="Search by policy number">
            <input autoFocus style={inputStyle} value={q} onChange={e => setQ(e.target.value)} placeholder="Type a policy number…" />
          </Field>
          <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid var(--border-light,#EEF1F4)", borderRadius: 5 }}>
            {hits.length === 0 ? (
              <div style={{ padding: 14, color: "var(--text3,#888)", fontStyle: "italic", fontSize: 13 }}>No matching policies.</div>
            ) : hits.map(h => (
              <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderBottom: "1px solid var(--border-light,#EEF1F4)" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{h.policy_number}</div>
                  <div style={{ fontSize: 11, color: "var(--text3,#888)" }}>
                    {h.policy_type || "—"} · {periodLabel(h.inception_date, h.expiration_date)} · on {h.claim_count} claim(s) · {h.source}
                  </div>
                </div>
                <button className="btn btn-xs btn-primary" disabled={busy} onClick={() => attach(h.id)}>Attach</button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <Field label="Policy number *">
            <input autoFocus style={inputStyle} value={form.policy_number} onChange={e => setForm({ ...form, policy_number: e.target.value })} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Policy type">
              <input style={inputStyle} value={form.policy_type} onChange={e => setForm({ ...form, policy_type: e.target.value })} placeholder="e.g. P&I" />
            </Field>
            <Field label="Layer">
              <select style={inputStyle} value={form.layer_type} onChange={e => setForm({ ...form, layer_type: e.target.value })}>
                <option value="">—</option>
                {LAYER_TYPES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Inception"><input type="date" style={inputStyle} value={form.inception_date} onChange={e => setForm({ ...form, inception_date: e.target.value })} /></Field>
            <Field label="Expiration"><input type="date" style={inputStyle} value={form.expiration_date} onChange={e => setForm({ ...form, expiration_date: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><textarea style={{ ...inputStyle, minHeight: 56 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <button className="btn btn-sm btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        {tab === "new" && <button className="btn btn-sm btn-primary" disabled={busy} onClick={createAndAttach}>Create & attach</button>}
      </div>
    </ModalShell>
  );
}

function PolicyEditModal({ policyId, onClose, onDone }) {
  const [p, setP] = useState(null);
  const [insureds, setInsureds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [cq, setCq] = useState("");
  const [chits, setChits] = useState([]);

  const reloadDetail = useCallback(() => {
    apiFetch(`/policies/${policyId}`).then(d => {
      const pol = d.policy;
      setP({
        policy_number: pol.policy_number || "", policy_type: pol.policy_type || "",
        layer_type: pol.layer_type || "",
        inception_date: pol.inception_date ? String(pol.inception_date).slice(0, 10) : "",
        expiration_date: pol.expiration_date ? String(pol.expiration_date).slice(0, 10) : "",
        year_of_account: pol.year_of_account ?? "",
        annual_aggregate_deductible: pol.annual_aggregate_deductible ?? "",
        fleet_program_note: pol.fleet_program_note || "", notes: pol.notes || "",
        source: pol.source,
      });
      setInsureds(d.insureds || []);
    }).catch(e => setErr(e.message));
  }, [policyId]);

  useEffect(() => { reloadDetail(); }, [reloadDetail]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!cq.trim()) { setChits([]); return; }
      apiFetch(`/companies?search=${encodeURIComponent(cq)}`).then(d => setChits((d || []).slice(0, 8))).catch(() => setChits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [cq]);

  if (!p) return <ModalShell title="Edit policy" onClose={onClose}><ErrBar msg={err} /><Spinner label="Loading policy details…" /></ModalShell>;

  const save = () => {
    setBusy(true); setErr(null);
    const body = {
      policy_number: p.policy_number, policy_type: p.policy_type, layer_type: p.layer_type,
      inception_date: p.inception_date, expiration_date: p.expiration_date,
      year_of_account: p.year_of_account === "" ? "" : Number(p.year_of_account),
      annual_aggregate_deductible: p.annual_aggregate_deductible,
      fleet_program_note: p.fleet_program_note, notes: p.notes,
    };
    apiFetch(`/policies/${policyId}`, { method: "PUT", body })
      .then(onDone).catch(e => { setErr(e.message || "Save failed."); setBusy(false); });
  };

  const addInsured = (companyId) => {
    apiFetch(`/policies/${policyId}/insureds`, { method: "POST", body: { insured_company_id: companyId } })
      .then(() => { setCq(""); setChits([]); reloadDetail(); })
      .catch(e => setErr(e.message || "Add insured failed."));
  };
  // WS0-B H22: this was the only ungated delete in this file — every sibling (detach policy,
  // remove coverage) already confirms — and the backend DELETE is HARD, with only
  // `insured_company_id` in the audit row (`is_primary` is lost). One misplaced click.
  const removeInsured = (id, name) => {
    if (!window.confirm(`Remove ${name || "this named insured"} from the policy?\n\nThis can't be undone — the named insured would have to be re-added, and its "primary" flag is not restored.`)) return;
    apiFetch(`/policies/${policyId}/insureds/${id}`, { method: "DELETE" })
      .then(reloadDetail).catch(e => setErr(e.message || "Remove failed."));
  };

  return (
    <ModalShell title="Edit policy" onClose={onClose} width={620}>
      <ErrBar msg={err} />
      {p.source === "vca_lift" && (
        <div style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A", padding: "8px 10px", borderRadius: 5, fontSize: 12, marginBottom: 12 }}>
          This policy was lifted from VCA. Saving an edit makes Atticus the owner of this record (the nightly sync will stop overwriting it).
        </div>
      )}
      <Field label="Policy number *"><input style={inputStyle} value={p.policy_number} onChange={e => setP({ ...p, policy_number: e.target.value })} /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Policy type"><input style={inputStyle} value={p.policy_type} onChange={e => setP({ ...p, policy_type: e.target.value })} /></Field>
        <Field label="Layer">
          <select style={inputStyle} value={p.layer_type} onChange={e => setP({ ...p, layer_type: e.target.value })}>
            <option value="">—</option>
            {LAYER_TYPES.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <Field label="Inception"><input type="date" style={inputStyle} value={p.inception_date} onChange={e => setP({ ...p, inception_date: e.target.value })} /></Field>
        <Field label="Expiration"><input type="date" style={inputStyle} value={p.expiration_date} onChange={e => setP({ ...p, expiration_date: e.target.value })} /></Field>
        <Field label="Year of account"><input type="number" style={inputStyle} value={p.year_of_account} onChange={e => setP({ ...p, year_of_account: e.target.value })} /></Field>
        <Field label="Annual aggregate deductible"><input type="number" step="0.01" style={inputStyle} value={p.annual_aggregate_deductible} onChange={e => setP({ ...p, annual_aggregate_deductible: e.target.value })} /></Field>
      </div>
      <Field label="Fleet / program note"><input style={inputStyle} value={p.fleet_program_note} onChange={e => setP({ ...p, fleet_program_note: e.target.value })} /></Field>
      <Field label="Notes"><textarea style={{ ...inputStyle, minHeight: 56 }} value={p.notes} onChange={e => setP({ ...p, notes: e.target.value })} /></Field>

      <div style={{ borderTop: "1px solid var(--border-light,#EEF1F4)", margin: "8px 0 12px", paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2,#555)", marginBottom: 8 }}>Insureds on this policy</div>
        {insureds.length === 0 && <div style={{ fontSize: 12, color: "var(--text3,#888)", fontStyle: "italic", marginBottom: 8 }}>None yet.</div>}
        {insureds.map(ins => (
          <div key={ins.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 13 }}>
            <span>{ins.company_name}{ins.is_primary && <Badge color="#2E75B6" text="Primary" />}<span style={{ fontSize: 11, color: "var(--text3,#888)", marginLeft: 8 }}>{ins.source}</span></span>
            <button className="btn btn-xs btn-outline" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }} onClick={() => removeInsured(ins.id, ins.company_name || ins.insured_name)}>Remove</button>
          </div>
        ))}
        <div style={{ marginTop: 8, position: "relative" }}>
          <input style={inputStyle} value={cq} onChange={e => setCq(e.target.value)} placeholder="Add insured — search companies…" />
          {chits.length > 0 && (
            <div style={{ position: "absolute", zIndex: 5, background: "white", border: "1px solid var(--border,#DDE2E8)", borderRadius: 5, width: "100%", maxHeight: 180, overflowY: "auto", boxShadow: "0 6px 18px rgba(0,0,0,0.15)" }}>
              {chits.map(c => (
                <div key={c.id} onClick={() => addInsured(c.id)} style={{ padding: "7px 10px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--border-light,#EEF1F4)" }}>
                  {c.company_name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* DC-3 (2026-06-17): policy document lens — slip, endorsements, coverage
          letters. Edit + view gated on manage_policies server-side. */}
      <div style={{ borderTop: "1px solid var(--border-light,#EEF1F4)", margin: "8px 0 12px", paddingTop: 12 }}>
        <EntityDocuments entityType="policy" entityId={policyId} canEdit={can("manage_policies")} defaultPhi={false} title="Policy documents" />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-sm btn-outline" onClick={onClose} disabled={busy}>Close</button>
        <button className="btn btn-sm btn-primary" onClick={save} disabled={busy}>Save policy</button>
      </div>
    </ModalShell>
  );
}

function CoverageFormModal({ claimId, coverage, defaultPolicyId, onClose, onDone }) {
  const editing = !!coverage;
  const [f, setF] = useState({
    coverage_type: coverage?.coverage_type || "",
    coverage_amount: coverage?.coverage_amount ?? "",
    per_claim_deductible: coverage?.per_claim_deductible ?? "",
    applies_to_loss: !!coverage?.applies_to_loss,
    notes: coverage?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = () => {
    if (!f.coverage_type.trim()) { setErr("Coverage type is required."); return; }
    setBusy(true); setErr(null);
    const body = { ...f };
    if (!editing && defaultPolicyId) body.policy_id = defaultPolicyId;
    const url = editing ? `/claims/${claimId}/coverages/${coverage.id}` : `/claims/${claimId}/coverages`;
    apiFetch(url, { method: editing ? "PUT" : "POST", body })
      .then(onDone).catch(e => { setErr(e.message || "Save failed."); setBusy(false); });
  };

  return (
    <ModalShell title={editing ? "Edit coverage line" : "Add coverage line"} onClose={onClose}>
      <ErrBar msg={err} />
      <Field label="Coverage type *"><input autoFocus style={inputStyle} value={f.coverage_type} onChange={e => setF({ ...f, coverage_type: e.target.value })} placeholder="e.g. P&I, Hull" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Limit / coverage amount"><input type="number" step="0.01" style={inputStyle} value={f.coverage_amount} onChange={e => setF({ ...f, coverage_amount: e.target.value })} /></Field>
        <Field label="Per-claim deductible"><input type="number" step="0.01" style={inputStyle} value={f.per_claim_deductible} onChange={e => setF({ ...f, per_claim_deductible: e.target.value })} /></Field>
      </div>
      <Field label="">
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={f.applies_to_loss} onChange={e => setF({ ...f, applies_to_loss: e.target.checked })} />
          This coverage applies to the loss
        </label>
      </Field>
      <Field label="Notes"><textarea style={{ ...inputStyle, minHeight: 56 }} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-sm btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-sm btn-primary" onClick={save} disabled={busy}>{editing ? "Save" : "Add"}</button>
      </div>
    </ModalShell>
  );
}

function DetachModal({ claimId, attachment, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const detach = () => {
    setBusy(true); setErr(null);
    apiFetch(`/claims/${claimId}/policies/${attachment.attachment_id}`, { method: "DELETE", body: { detach_reason: reason } })
      .then(onDone).catch(e => { setErr(e.message || "Detach failed."); setBusy(false); });
  };
  return (
    <ModalShell title="Detach policy" onClose={onClose} width={460}>
      <ErrBar msg={err} />
      <p style={{ fontSize: 13, color: "var(--text2,#555)", marginTop: 0 }}>
        Detach <strong>{attachment.policy_number}</strong> from this claim? The policy record itself is kept; only the attachment is removed.
      </p>
      <Field label="Reason (optional)"><input autoFocus style={inputStyle} value={reason} onChange={e => setReason(e.target.value)} /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-sm btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-sm btn-primary" style={{ background: "#B91C1C", borderColor: "#B91C1C" }} onClick={detach} disabled={busy}>Detach</button>
      </div>
    </ModalShell>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function Th({ children, align }) {
  return <th style={{
    background: "var(--bg,#F8F9FB)", color: "var(--text2,#555)",
    textAlign: align || "left", padding: "8px 12px",
    fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600,
  }}>{children}</th>;
}
function Td({ children, align, style }) {
  return <td style={{
    padding: "8px 12px", textAlign: align || "left",
    fontFamily: align === "right" ? "monospace" : undefined,
    ...style,
  }}>{children}</td>;
}
function FieldGrid({ rows }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "160px 1fr",
      rowGap: 6, columnGap: 16, fontSize: 14,
    }}>
      {rows.map(([label, value], i) => (
        <FieldRow key={i} label={label} value={value} />
      ))}
    </div>
  );
}
function FieldRow({ label, value }) {
  return (
    <>
      <div style={{ color: "var(--text2,#555)", fontWeight: 500 }}>{label}</div>
      <div>{value}</div>
    </>
  );
}
function Badge({ color, text }) {
  return <span style={{
    background: color, color: "white", fontSize: 10,
    padding: "2px 6px", borderRadius: 3,
    textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600,
    marginLeft: 8,
  }}>{text}</span>;
}
function muted(text) {
  return <span style={{ color: "var(--text3,#888)", fontStyle: "italic" }}>{text}</span>;
}
function periodLabel(incep, expir) {
  if (!incep && !expir) return muted("No period recorded");
  if (incep && expir) return `${fmt.date(incep)} – ${fmt.date(expir)}`;
  if (incep)          return `From ${fmt.date(incep)}`;
  return `Through ${fmt.date(expir)}`;
}
function limitDisplay(amount) {
  if (amount == null || Number(amount) === 0) {
    return <span style={{ color: "var(--text3,#888)" }} title="Not recorded in source data.">—</span>;
  }
  return fmt.money(amount);
}
function deductibleDisplay(amount) {
  if (amount == null) return <span style={{ color: "var(--text3,#888)" }}>—</span>;
  return fmt.money(amount);
}
